import { ProductDetailsCart } from '../models';

/**
 * Shopping Dollars are fixed at 1 point = 1 USD. Points are whole numbers, so cents are dropped (floor).
 * Returns 0 when usd is non-finite.
 */
export function usdToPoints(usd: number): number {
  if (!Number.isFinite(usd)) return 0;
  return Math.floor(usd);
}

export function computeCartSubtotalUsd(productCard: ProductDetailsCart | null): number {
  return productCard?.subtotalDiscountedPrice ?? productCard?.subtotalPrice ?? 0;
}

export const pickLowestValue = (val1: number, val2: number): number => {
  return val1 > val2 ? val2 : val1;
};

/**
 * Cart total in points. Each line drops its own cents, like the backend checkout calculator,
 * so the points can only ever cover whole dollars of a line and its cents go to the card.
 *
 * @param productCard - The cart data containing products
 * @param useDiscountedPrice - Whether to use discounted prices or regular prices
 */
export function calculateCartTotalPoints(productCard: ProductDetailsCart | null, useDiscountedPrice = false): number {
  const products = productCard?.products ?? [];

  return products.reduce((total, product) => {
    // `price`/`discountedPrice` are line totals (already include quantity).
    const price = useDiscountedPrice && product.discountedPrice ? product.discountedPrice : product.price;
    return total + usdToPoints(price);
  }, 0);
}

/**
 * Formats a price as whole Shopping Dollars with the reward name, e.g. "1,299 Shopping Dollars".
 * Prices are loaded in Shopping Dollars, so the amount is only truncated (like DigitsGroupingPipe), not converted.
 * Accepts strings such as "199.00" or "$0.00".
 */
export function formatRewardAmount(amount: number | string | null | undefined, rewardName: string): string {
  const value = typeof amount === 'string' ? Number.parseFloat(amount.replace(/[^0-9.-]/g, '')) : (amount ?? 0);
  const whole = Number.isFinite(value) ? Math.trunc(value) : 0;
  return `${whole.toLocaleString('en-US')} ${rewardName}`.trim();
}
